import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  Assessment as AssessmentIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

interface User {
  id: string;
  email: string;
  name: string;
  workspace_id: string;
  workspace_name: string;
  role: string;
  created_at: string;
}

interface AuditLog {
  id: string;
  workspace_id: string;
  workspace_name: string;
  user_id: string | null;
  user_email: string | null;
  action: string;
  entity_type: string;
  entity_id: string;
  changes: any;
  timestamp: string;
}

interface Workspace {
  id: string;
  name: string;
  slug: string;
  plan_tier: string;
  user_count: number;
  created_at: string;
  updated_at: string;
}

interface SystemStats {
  total_workspaces: number;
  active_workspaces: number;
  total_users: number;
  users_by_role: Record<string, number>;
  total_campaigns: number;
  total_leads: number;
  total_content: number;
  avg_users_per_workspace: number;
}

export default function Admin() {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Users state
  const [users, setUsers] = useState<User[]>([]);
  const [usersPage, setUsersPage] = useState(0);
  const [usersRowsPerPage, setUsersRowsPerPage] = useState(25);
  const [userRoleFilter, setUserRoleFilter] = useState('');
  const [userWorkspaceFilter, setUserWorkspaceFilter] = useState('');

  // Audit logs state
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [logsPage, setLogsPage] = useState(0);
  const [logsRowsPerPage, setLogsRowsPerPage] = useState(25);
  const [logActionFilter, setLogActionFilter] = useState('');
  const [logEntityFilter, setLogEntityFilter] = useState('');

  // Workspaces state
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [workspacesPage, setWorkspacesPage] = useState(0);
  const [workspacesRowsPerPage, setWorkspacesRowsPerPage] = useState(25);

  // Stats state
  const [stats, setStats] = useState<SystemStats | null>(null);

  useEffect(() => {
    loadData();
  }, [tabValue, usersPage, logsPage, workspacesPage, userRoleFilter, logActionFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (tabValue === 0) {
        // Load stats
        const response = await axios.get(`${API_URL}/admin/stats`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        });
        setStats(response.data);
      } else if (tabValue === 1) {
        // Load users
        const params: any = {
          skip: usersPage * usersRowsPerPage,
          limit: usersRowsPerPage,
        };
        if (userRoleFilter) params.role = userRoleFilter;
        if (userWorkspaceFilter) params.workspace_id = userWorkspaceFilter;

        const response = await axios.get(`${API_URL}/admin/users`, {
          params,
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        });
        setUsers(response.data);
      } else if (tabValue === 2) {
        // Load workspaces
        const response = await axios.get(`${API_URL}/admin/workspaces`, {
          params: {
            skip: workspacesPage * workspacesRowsPerPage,
            limit: workspacesRowsPerPage,
          },
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        });
        setWorkspaces(response.data);
      } else if (tabValue === 3) {
        // Load audit logs
        const params: any = {
          skip: logsPage * logsRowsPerPage,
          limit: logsRowsPerPage,
        };
        if (logActionFilter) params.action = logActionFilter;
        if (logEntityFilter) params.entity_type = logEntityFilter;

        const response = await axios.get(`${API_URL}/admin/audit-logs`, {
          params,
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        });
        setAuditLogs(response.data);
      }
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const getRoleColor = (role: string): any => {
    const colors: any = {
      admin: 'error',
      manager: 'warning',
      member: 'primary',
      viewer: 'default',
    };
    return colors[role.toLowerCase()] || 'default';
  };

  const getPlanColor = (plan: string): any => {
    const colors: any = {
      enterprise: 'error',
      professional: 'warning',
      starter: 'primary',
      free: 'default',
    };
    return colors[plan.toLowerCase()] || 'default';
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        mb: { xs: 2, sm: 3 },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>System Administration</Typography>
        <Tooltip title="Refresh Data">
          <IconButton onClick={loadData} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 3 }}>
        <Tab icon={<AssessmentIcon />} label="Overview" />
        <Tab icon={<PeopleIcon />} label="Users" />
        <Tab icon={<BusinessIcon />} label="Workspaces" />
        <Tab icon={<HistoryIcon />} label="Audit Logs" />
      </Tabs>

      {/* Overview Tab */}
      {tabValue === 0 && (
        <Box>
          {loading && !stats ? (
            <Box display="flex" justifyContent="center" py={8}>
              <CircularProgress />
            </Box>
          ) : stats ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Workspaces
                    </Typography>
                    <Typography variant="h4">{stats.total_workspaces}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {stats.active_workspaces} active (30 days)
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Users
                    </Typography>
                    <Typography variant="h4">{stats.total_users}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Avg {stats.avg_users_per_workspace} per workspace
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Campaigns
                    </Typography>
                    <Typography variant="h4">{stats.total_campaigns}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Leads
                    </Typography>
                    <Typography variant="h4">{stats.total_leads}</Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Users by Role
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      {Object.entries(stats.users_by_role).map(([role, count]) => (
                        <Box key={role} display="flex" justifyContent="space-between" mb={1}>
                          <Chip label={role} color={getRoleColor(role)} size="small" />
                          <Typography variant="h6">{count}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Content Statistics
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography>Generated Content</Typography>
                        <Typography variant="h6">{stats.total_content}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography>Avg Content per Workspace</Typography>
                        <Typography variant="h6">
                          {Math.round(stats.total_content / stats.total_workspaces)}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : null}
        </Box>
      )}

      {/* Users Tab */}
      {tabValue === 1 && (
        <Box>
          <Box display="flex" gap={2} mb={2}>
            <TextField
              select
              label="Filter by Role"
              value={userRoleFilter}
              onChange={(e) => setUserRoleFilter(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All Roles</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="manager">Manager</MenuItem>
              <MenuItem value="member">Member</MenuItem>
              <MenuItem value="viewer">Viewer</MenuItem>
            </TextField>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Workspace</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Created At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : users.length > 0 ? (
                  users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.workspace_name}</TableCell>
                      <TableCell>
                        <Chip label={user.role} color={getRoleColor(user.role)} size="small" />
                      </TableCell>
                      <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No users found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={-1}
            page={usersPage}
            onPageChange={(_, newPage) => setUsersPage(newPage)}
            rowsPerPage={usersRowsPerPage}
            onRowsPerPageChange={(e) => setUsersRowsPerPage(parseInt(e.target.value, 10))}
          />
        </Box>
      )}

      {/* Workspaces Tab */}
      {tabValue === 2 && (
        <Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Slug</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Users</TableCell>
                  <TableCell>Created At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : workspaces.length > 0 ? (
                  workspaces.map((workspace) => (
                    <TableRow key={workspace.id}>
                      <TableCell>{workspace.name}</TableCell>
                      <TableCell>{workspace.slug}</TableCell>
                      <TableCell>
                        <Chip label={workspace.plan_tier} color={getPlanColor(workspace.plan_tier)} size="small" />
                      </TableCell>
                      <TableCell>{workspace.user_count}</TableCell>
                      <TableCell>{new Date(workspace.created_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No workspaces found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={-1}
            page={workspacesPage}
            onPageChange={(_, newPage) => setWorkspacesPage(newPage)}
            rowsPerPage={workspacesRowsPerPage}
            onRowsPerPageChange={(e) => setWorkspacesRowsPerPage(parseInt(e.target.value, 10))}
          />
        </Box>
      )}

      {/* Audit Logs Tab */}
      {tabValue === 3 && (
        <Box>
          <Box display="flex" gap={2} mb={2}>
            <TextField
              select
              label="Filter by Action"
              value={logActionFilter}
              onChange={(e) => setLogActionFilter(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All Actions</MenuItem>
              <MenuItem value="create">Create</MenuItem>
              <MenuItem value="update">Update</MenuItem>
              <MenuItem value="delete">Delete</MenuItem>
              <MenuItem value="login">Login</MenuItem>
              <MenuItem value="logout">Logout</MenuItem>
            </TextField>

            <TextField
              select
              label="Filter by Entity"
              value={logEntityFilter}
              onChange={(e) => setLogEntityFilter(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All Entities</MenuItem>
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="workspace">Workspace</MenuItem>
              <MenuItem value="campaign">Campaign</MenuItem>
              <MenuItem value="lead">Lead</MenuItem>
              <MenuItem value="content">Content</MenuItem>
            </TextField>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Entity</TableCell>
                  <TableCell>Workspace</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : auditLogs.length > 0 ? (
                  auditLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>{new Date(log.timestamp).toLocaleString()}</TableCell>
                      <TableCell>{log.user_email || 'System'}</TableCell>
                      <TableCell>
                        <Chip label={log.action} size="small" />
                      </TableCell>
                      <TableCell>{log.entity_type}</TableCell>
                      <TableCell>{log.workspace_name}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No audit logs found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={-1}
            page={logsPage}
            onPageChange={(_, newPage) => setLogsPage(newPage)}
            rowsPerPage={logsRowsPerPage}
            onRowsPerPageChange={(e) => setLogsRowsPerPage(parseInt(e.target.value, 10))}
          />
        </Box>
      )}
    </Box>
  );
}
